import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { addProduct, getCategoriesList } from "../../../../../services/Admin/MenuApi";

const initialForm = {
  name: "",
  description: "",
  price: "",
  category_id: "",
  prepare_time: "",
  image: null,
};

const AddProductDialoge = ({ open, onClose, onAdded }) => {
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await getCategoriesList();
        if (mounted) setCategories(res?.data || []);
      } catch (err) {
        // Categories are optional here; the API validation will handle missing values.
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    };

    if (open) loadCategories();
    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setError("");
      setSuccessMsg("");
    }
  }, [open]);

  const handleChange = (key) => (event) => {
    const value = key === "image" ? event.target.files?.[0] || null : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const cleanName = String(form.name || "").trim();
      const cleanDescription = String(form.description || "").trim();

      const res = await addProduct({
        name: cleanName,
        name_en: cleanName,
        description: cleanDescription,
        description_en: cleanDescription,
        price: form.price,
        category_id: form.category_id,
        image: form.image,
        prepare_time: form.prepare_time,
      });

      onAdded?.(res?.data || null);
      setSuccessMsg("تمت إضافة الصنف بنجاح");
      setForm(initialForm);
    } catch (err) {
      setError(err?.message || "فشل إضافة الصنف");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>إضافة صنف جديد</DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, pt: 0.5 }}>
          {successMsg && <Alert severity="success">{successMsg}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="الاسم الجديد"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            required
          />

          <TextField
            label="الوصف الجديد"
            value={form.description}
            onChange={handleChange("description")}
            fullWidth
            multiline
            rows={2}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="السعر"
              value={form.price}
              onChange={handleChange("price")}
              fullWidth
            />

            <TextField
              label="زمن التحضير (دقيقة)"
              value={form.prepare_time}
              onChange={handleChange("prepare_time")}
              fullWidth
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id="add-product-category-label">الفئة</InputLabel>
            <Select
              labelId="add-product-category-label"
              value={form.category_id}
              label="الفئة"
              onChange={handleChange("category_id")}
              disabled={loadingCategories}
            >
              <MenuItem value="">اختر الفئة</MenuItem>
              {loadingCategories && <MenuItem disabled>جاري تحميل الفئات...</MenuItem>}
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              صورة الصنف
            </Typography>
            <input type="file" accept="image/*" onChange={handleChange("image")} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          إلغاء
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : "إضافة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductDialoge;
